import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertCircle, Pencil, Plus, Store, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", address: "", phone: "", active: true };

export default function Outlets() {
  const { entitlements, refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => setRows((await api.get("/outlets")).data || []);
  useEffect(() => { load(); }, []);

  const activeCount = useMemo(() => rows.filter((row) => row.active !== false).length, [rows]);
  const maxOutlets = Number(entitlements?.max_outlets || 0);
  const atLimit = maxOutlets > 0 && activeCount >= maxOutlets;

  const openCreate = () => {
    if (atLimit) return toast.error(`Batas outlet paket tercapai (${activeCount}/${maxOutlets})`);
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) await api.patch(`/outlets/${editId}`, form);
      else await api.post("/outlets", form);
      toast.success("Outlet tersimpan");
      setOpen(false);
      setForm(empty);
      setEditId(null);
      await load();
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Outlet gagal disimpan");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus outlet?")) return;
    try {
      await api.delete(`/outlets/${id}`);
      toast.success("Outlet terhapus");
      await load();
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Outlet gagal dihapus");
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <div className="overline text-zinc-500">CABANG</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Outlet</h1>
          <div className="text-sm text-zinc-500 mt-2 tabular-nums">
            Penggunaan paket: <strong className="text-zinc-900">{activeCount}/{maxOutlets || "-"}</strong> outlet aktif
          </div>
        </div>
        <button onClick={openCreate} disabled={atLimit || !entitlements?.available} data-testid="outlet-add-btn" className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus className="h-4 w-4" /> Outlet Baru
        </button>
      </div>

      {(atLimit || !entitlements?.available) && (
        <div className="mb-5 border border-amber-300 bg-amber-50 p-4 flex gap-3 text-sm text-amber-900">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">{entitlements?.available ? "Kuota outlet paket sudah penuh" : "Langganan tidak aktif"}</div>
            <div className="mt-1">{entitlements?.available ? "Nonaktifkan outlet yang tidak digunakan atau ubah paket untuk menambah outlet baru." : entitlements?.reason}</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.length === 0 && <div className="p-10 col-span-3 text-center text-zinc-500 border border-border">Belum ada outlet</div>}
        {rows.map((outlet) => (
          <div key={outlet.id} className={`p-6 bg-white border ${outlet.active === false ? "border-dashed border-zinc-300 opacity-70" : "border-border"}`} data-testid={`outlet-card-${outlet.id}`}>
            <div className="flex items-start justify-between">
              <Store className="h-6 w-6 text-[#002FA7]" />
              <div className="flex gap-1">
                <button onClick={() => {
                  setForm({ name: outlet.name, address: outlet.address || "", phone: outlet.phone || "", active: outlet.active !== false });
                  setEditId(outlet.id);
                  setOpen(true);
                }} className="p-1.5 hover:bg-zinc-100" data-testid={`outlet-edit-${outlet.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(outlet.id)} className="p-1.5 hover:bg-rose-100 text-rose-600" data-testid={`outlet-del-${outlet.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <div className="font-display font-bold text-xl tracking-tight">{outlet.name}</div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${outlet.active === false ? "bg-zinc-200 text-zinc-600" : "bg-emerald-100 text-emerald-800"}`}>{outlet.active === false ? "Nonaktif" : "Aktif"}</span>
            </div>
            <div className="text-sm text-zinc-500 mt-1">{outlet.address || "—"}</div>
            <div className="text-xs text-zinc-500 mt-2">{outlet.phone || ""}</div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Buat"} Outlet</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="overline">Nama</label><input data-testid="outlet-form-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" /></div>
            <div><label className="overline">Alamat</label><textarea data-testid="outlet-form-address" value={form.address} onChange={(event) => setForm({ ...form, address: event.target.value })} rows={3} className="w-full border border-border px-3 py-2 mt-1" /></div>
            <div><label className="overline">Telepon</label><input data-testid="outlet-form-phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" /></div>
            {editId && (
              <label className="flex items-center justify-between border border-border p-3 cursor-pointer">
                <div><div className="text-sm font-semibold">Outlet aktif</div><div className="text-xs text-zinc-500">Outlet nonaktif tidak menggunakan kuota paket.</div></div>
                <input type="checkbox" checked={form.active !== false} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
              </label>
            )}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setOpen(false)} className="flex-1 py-3 border border-border">Batal</button>
            <button onClick={save} data-testid="outlet-save-btn" className="flex-1 py-3 bg-[#002FA7] text-white font-semibold">Simpan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
